// Hub /match — entry point unico del consulente per tutti i suoi match.
// Tab "Da segnalare": draft auto_created raggruppati per lotto (stessa logica
// della MatchBacklogCard ma TUTTI i lotti) con Segnala / Scarta.
// Tab "Segnalati": proposte sent/read/visit_scheduled/visit_done rese con
// ProposalCard (follow-up inline gia' presente).
//
// NOTA status comma-list: il backend list_proposals (proposals.py) splitta
// `status` sulle virgole e usa Proposal.status.in_([...]). Quindi la singola
// query con status="sent,read,visit_scheduled,visit_done" e' supportata.
import { useMemo, useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { hasTokens } from '../lib/auth'
import type { ProposalRow } from '../lib/api'
import { useProposalsList, useTransitionProposal } from '../hooks/useProposals'
import { groupByLotto, type LottoGroup } from '../lib/matchGrouping'
import {
  daysToAuction,
  isOverdue,
  isUrgent,
  overdueDays,
} from '../lib/proposalUrgency'
import { ProposalCard } from '../components/proposal/ProposalCard'
import { ProposalPreviewModal } from '../components/proposal/ProposalPreviewModal'

export const Route = createFileRoute('/match')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  component: MatchHubPage,
})

type Tab = 'da-segnalare' | 'segnalati'

function scoreColor(score: number): string {
  if (score >= 85) return 'var(--ok-ink, #1a8a3a)'
  if (score >= 70) return 'var(--accent-ink, #cc7a00)'
  return 'var(--ink-3)'
}

function lottoCode(code: number): string {
  return `L_${String(code).padStart(5, '0')}`
}

function MatchHubPage() {
  const [tab, setTab] = useState<Tab>('da-segnalare')

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            Operativo · <span className="here">Match</span>
          </div>
          <div className="page-title-row">
            <h1>I miei match</h1>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-1)',
          borderBottom: '1px solid var(--line)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <TabButton
          active={tab === 'da-segnalare'}
          onClick={() => setTab('da-segnalare')}
          label="🔔 Da segnalare"
        />
        <TabButton
          active={tab === 'segnalati'}
          onClick={() => setTab('segnalati')}
          label="📤 Segnalati"
        />
      </div>

      {tab === 'da-segnalare' ? <DaSegnalareTab /> : <SegnalatiTab />}
    </>
  )
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 0,
        padding: '8px 16px',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        color: active ? 'var(--ink-1)' : 'var(--ink-3)',
        cursor: 'pointer',
        fontSize: 'var(--fs-13)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
      }}
    >
      {label}
    </button>
  )
}

function UrgencyBadge({ group }: { group: LottoGroup }) {
  const representative = group.clients[0]
  if (!representative) return null
  if (isUrgent(representative)) {
    const days = daysToAuction(representative) ?? 0
    return (
      <span className="chip warn" style={{ fontSize: 'var(--fs-10)' }}>
        🔴 Asta {days}gg
      </span>
    )
  }
  const overdueClient = group.clients.find((c) => isOverdue(c))
  if (overdueClient) {
    return (
      <span className="chip risk" style={{ fontSize: 'var(--fs-10)' }}>
        ⏰ Scaduto {overdueDays(overdueClient)}gg
      </span>
    )
  }
  return null
}

function DaSegnalareTab() {
  const navigate = useNavigate()
  const { data, isPending, isError, error } = useProposalsList({
    status: 'draft',
    auto_created: true,
    page_size: 200,
    sort: '-created_at',
  })
  const transition = useTransitionProposal()
  const [previewProposal, setPreviewProposal] = useState<ProposalRow | null>(null)

  const items = useMemo(() => data?.items ?? [], [data])
  const groups = useMemo(() => groupByLotto(items), [items])

  function handleScarta(p: ProposalRow) {
    const name = p.client_name || 'questo cliente'
    if (window.confirm(`Scartare il match per ${name}?`)) {
      transition.mutate({ id: p.id, body: { to: 'rejected' } })
    }
  }

  if (isPending) return <div className="muted">Caricamento…</div>
  if (isError) return <div className="error">{error?.message ?? 'Errore'}</div>
  if (groups.length === 0) {
    return (
      <div className="muted" style={{ padding: 'var(--sp-4)' }}>
        Nessun match da segnalare. Nuovi match arriveranno automaticamente.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      {groups.map((group) => (
        <div
          key={group.property_id}
          style={{
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            padding: 'var(--sp-3)',
            background: 'var(--surface)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 'var(--sp-2)',
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--sp-1)' }}>
              <span style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-13)' }}>
                {group.property_address || 'Indirizzo non disponibile'}
              </span>
              <span className="muted" style={{ fontSize: 'var(--fs-11)', fontFamily: 'var(--font-mono)' }}>
                {lottoCode(group.property_short_code)}
              </span>
              <UrgencyBadge group={group} />
            </div>
            <button
              type="button"
              className="btn sm ghost"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() =>
                void navigate({
                  to: '/lotti/$id',
                  params: { id: group.property_id },
                } as never)
              }
            >
              → Apri lotto
            </button>
          </div>

          <div className="muted" style={{ fontSize: 'var(--fs-11)' }}>
            {group.clients.length} client
            {group.clients.length === 1 ? 'e' : 'i'} interessat
            {group.clients.length === 1 ? 'o' : 'i'}
          </div>

          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
            {group.clients.map((c) => (
              <li
                key={c.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--sp-2)',
                  padding: '4px 8px',
                  borderRadius: 'var(--r-sm)',
                  background: 'var(--bg-2)',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', minWidth: 0 }}>
                  {c.match_score != null && (
                    <span
                      style={{
                        fontWeight: 'var(--fw-semibold)',
                        fontSize: 'var(--fs-12)',
                        fontFamily: 'var(--font-mono)',
                        color: scoreColor(c.match_score),
                      }}
                    >
                      {c.match_score}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 'var(--fs-12)',
                      color: 'var(--ink-2)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.client_name || 'Cliente'}
                  </span>
                </span>
                <span style={{ display: 'flex', gap: 'var(--sp-1)', flexShrink: 0 }}>
                  <button type="button" className="btn sm primary" onClick={() => setPreviewProposal(c)}>
                    Segnala
                  </button>
                  <button
                    type="button"
                    className="btn sm ghost"
                    disabled={transition.isPending}
                    onClick={() => handleScarta(c)}
                  >
                    Scarta
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {previewProposal && (
        <ProposalPreviewModal
          proposal={previewProposal}
          clientEmail={null}
          clientPhone={previewProposal.client_phone ?? null}
          onClose={() => setPreviewProposal(null)}
        />
      )}
    </div>
  )
}

function SegnalatiTab() {
  const { data, isPending, isError, error } = useProposalsList({
    status: 'sent,read,visit_scheduled,visit_done',
    page_size: 100,
    sort: '-created_at',
  })

  const items = data?.items ?? []

  if (isPending) return <div className="muted">Caricamento…</div>
  if (isError) return <div className="error">{error?.message ?? 'Errore'}</div>
  if (items.length === 0) {
    return (
      <div className="muted" style={{ padding: 'var(--sp-4)' }}>
        Nessun match segnalato.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      {items.map((p) => (
        <ProposalCard key={p.id} proposal={p} onOpen={() => {}} />
      ))}
    </div>
  )
}
